// components/common/BuyerDetailsModal.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, MapPin, Phone, Mail, Heart, XCircle, ChevronDown, ChevronLeft, ChevronRight, Inbox, MessageCircle, HelpCircle, Lock, Send } from 'react-feather';
import PropertyDetailsModal from './PropertyDetailsModal';
import AuthService from '../../services/authService';
import LauftLogo from '../../assets/LogoWhiteCropped.png';
import LauftLogoDark from '../../assets/LogoBlackCropped.png';
import darkIcon from '../../assets/dark-iconp.png';

const BuyerDetailsModal = ({ buyer, isOpen, onClose, lightMode = true, onMessage, onCurateMatch }) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showingsCount, setShowingsCount] = useState(0);
  const [likesPage, setLikesPage] = useState(0);
  const [dislikesPage, setDislikesPage] = useState(0);
  const [likesExpanded, setLikesExpanded] = useState(false);
  const [passedExpanded, setPassedExpanded] = useState(false);
  const [questionsExpanded, setQuestionsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [userQuestions, setUserQuestions] = useState([]);
  const [questionsPage, setQuestionsPage] = useState(0);
  const [isMarkingComplete, setIsMarkingComplete] = useState(null);
  const [openFilterSection, setOpenFilterSection] = useState('core'); // 'core' | 'home' | 'advanced' | 'rental' | null

  // Lauft bot transcript (read-only) — backend: GET /messages/lauft-bot/:userId
  const [botMessages, setBotMessages] = useState([]);
  const [botLoading, setBotLoading] = useState(false);
  const [showBotTranscript, setShowBotTranscript] = useState(false);
  
  // Light mode permanent (dark mode removed from portal)
  const [isLightMode] = useState(true);

  // ============================================
  // PRECAUTION MODE — release agent info to user
  // The buyer prop carries `agentInfoReleased` (default true; false only when
  // the claiming agent had precautionMode on). We mirror it locally so the
  // banner reflects the result of a release immediately, without waiting for
  // the parent to refetch the buyer.
  // ============================================
  const [agentInfoReleased, setAgentInfoReleased] = useState(
    buyer?.agentInfoReleased !== false
  );
  const [isReleasingInfo, setIsReleasingInfo] = useState(false);
  const [releaseError, setReleaseError] = useState('');

  useEffect(() => {
    // Resync when the modal opens with a different buyer.
    setAgentInfoReleased(buyer?.agentInfoReleased !== false);
    setReleaseError('');
  }, [buyer?._id, buyer?.agentInfoReleased]);

  const handleReleaseAgentInfo = async () => {
    if (!buyer?._id || isReleasingInfo) return;
    setIsReleasingInfo(true);
    setReleaseError('');

    try {
      const response = await fetch(
        `https://back-end-x7tx.onrender.com/api/agents/release-agent-info/${buyer._id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Flip locally — banner disappears.
        setAgentInfoReleased(true);
      } else {
        setReleaseError(data.message || 'Failed to release info to user');
      }
    } catch (err) {
      setReleaseError('Network error. Please try again.');
    } finally {
      setIsReleasingInfo(false);
    }
  };

  useEffect(() => {
    if (isOpen && buyer) {
      fetchShowingsCount();
      fetchUserQuestions();
      // Reset pages when modal opens with new buyer
      setLikesPage(0);
      setDislikesPage(0);
    }
    // Drop any transcript from a previously-viewed buyer so it can't bleed through
    setBotMessages([]);
    setShowBotTranscript(false);
  }, [isOpen, buyer]);

  // Fetch this buyer's Lauft assistant conversation (read-only, lazy).
  // Backend guards access — a claimed buyer is assigned to this agent, so it passes.
  const fetchBotConversation = async () => {
    if (!buyer?._id) return;
    setBotLoading(true);
    setShowBotTranscript(true);
    try {
      const res = await fetch(
        `https://back-end-x7tx.onrender.com/api/messages/lauft-bot/${buyer._id}`,
        {
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const json = res.ok ? await res.json() : { data: { messages: [] } };
      setBotMessages(json.data?.messages || []);
    } catch (err) {
      console.error('Error fetching bot conversation:', err);
      setBotMessages([]);
    } finally {
      setBotLoading(false);
    }
  };

  const fetchUserQuestions = async () => {
    if (!buyer?._id) return;
    
    try {
      const response = await fetch(
        `https://back-end-x7tx.onrender.com/api/questions/user/${buyer._id}/questions`,
        {
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUserQuestions(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching user questions:', err);
    }
  };

  const fetchShowingsCount = async () => {
    if (!buyer?._id) return;
    
    try {
      const showingsResponse = await fetch(
        `https://back-end-x7tx.onrender.com/api/showings?userId=${buyer._id}`,
        {
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (showingsResponse.ok) {
        const showingsData = await showingsResponse.json();
        setShowingsCount(showingsData.data?.length || 0);
      }
      
    } catch (err) {
      console.error('Error fetching showings count:', err);
    }
  };

  const markQuestionComplete = async (questionId) => {
    setIsMarkingComplete(questionId);
    try {
      const response = await fetch(
        `https://back-end-x7tx.onrender.com/api/questions/questions/${questionId}/respond`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ response: 'Responded to by agent' })
        }
      );
      
      if (response.ok) {
        setUserQuestions(prev => prev.map(q => 
          q._id === questionId 
            ? { ...q, status: 'answered', response: { text: 'Responded to by agent', respondedAt: new Date() } }
            : q
        ));
      }
    } catch (err) {
      console.error('Error marking question complete:', err);
    } finally {
      setIsMarkingComplete(null);
    }
  };

  if (!isOpen || !buyer) return null;

  const tierEmoji = {
    flex: '⚪',
    bronze: '🥉',
    silver: '🥈',
    gold: '🥇',
    platinum: '💎'
  };

  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
    setShowPropertyModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `$${amount.toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phone;
  };

  const renderPagination = (currentPage, totalItems, perPage, setPage) => {
    const totalPages = Math.ceil(totalItems / perPage);
    if (totalPages <= 1) return null;

    const hasPrev = currentPage > 0;
    const hasNext = currentPage < totalPages - 1;

    // Generate page numbers to show
    const getPageNumbers = () => {
      const pages = [];
      if (totalPages <= 5) {
        for (let i = 0; i < totalPages; i++) pages.push(i);
      } else {
        pages.push(0);
        if (currentPage > 2) pages.push('...');
        const start = Math.max(1, currentPage - 1);
        const end = Math.min(totalPages - 2, currentPage + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (currentPage < totalPages - 3) pages.push('...');
        pages.push(totalPages - 1);
      }
      return pages;
    };

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        marginTop: '20px',
        padding: '8px 0',
      }}>
        {/* Prev arrow */}
        <div
          onClick={(e) => { e.stopPropagation(); if (hasPrev) setPage(prev => prev - 1); }}
          style={{
            width: '34px', height: '34px',
            borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: hasPrev ? 'pointer' : 'default',
            opacity: hasPrev ? 1 : 0.3,
            backgroundColor: hasPrev 
              ? (isLightMode ? 'rgba(26, 26, 26, 0.05)' : 'rgba(255, 255, 255, 0.06)')
              : 'transparent',
            transition: 'all 0.2s',
            userSelect: 'none',
          }}
          onMouseEnter={(e) => { if (hasPrev) e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.1)' : 'rgba(255, 255, 255, 0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = hasPrev ? (isLightMode ? 'rgba(26, 26, 26, 0.05)' : 'rgba(255, 255, 255, 0.06)') : 'transparent'; }}
        >
          <ChevronLeft size={16} color={isLightMode ? '#1a1a1a' : '#ffffff'} />
        </div>

        {/* Page numbers */}
        {getPageNumbers().map((page, idx) => (
          page === '...' ? (
            <span key={`dots-${idx}`} style={{
              width: '34px', height: '34px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem',
              color: isLightMode ? 'rgba(26, 26, 26, 0.35)' : 'rgba(255, 255, 255, 0.35)',
              userSelect: 'none',
            }}>…</span>
          ) : (
            <div
              key={page}
              onClick={(e) => { e.stopPropagation(); setPage(page); }}
              className={currentPage === page ? 'buyer-modal-page-active' : ''}
              style={{
                width: '34px', height: '34px',
                borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'all 0.2s',
                background: currentPage === page 
                  ? (isLightMode ? 'rgba(26, 26, 26, 0.85)' : 'rgba(255, 255, 255, 0.15)')
                  : 'transparent',
              }}
              onMouseEnter={(e) => { 
                if (currentPage !== page) e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.08)' : 'rgba(255, 255, 255, 0.08)'; 
              }}
              onMouseLeave={(e) => { 
                if (currentPage !== page) e.currentTarget.style.backgroundColor = 'transparent'; 
              }}
            >
              <span style={{
                fontSize: '0.8rem',
                fontWeight: currentPage === page ? '700' : '500',
                color: currentPage === page ? '#ffffff' : (isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'),
                WebkitTextFillColor: currentPage === page ? '#ffffff' : undefined,
              }}>{page + 1}</span>
            </div>
          )
        ))}

        {/* Next arrow */}
        <div
          onClick={(e) => { e.stopPropagation(); if (hasNext) setPage(prev => prev + 1); }}
          style={{
            width: '34px', height: '34px',
            borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: hasNext ? 'pointer' : 'default',
            opacity: hasNext ? 1 : 0.3,
            backgroundColor: hasNext 
              ? (isLightMode ? 'rgba(26, 26, 26, 0.05)' : 'rgba(255, 255, 255, 0.06)')
              : 'transparent',
            transition: 'all 0.2s',
            userSelect: 'none',
          }}
          onMouseEnter={(e) => { if (hasNext) e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.1)' : 'rgba(255, 255, 255, 0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = hasNext ? (isLightMode ? 'rgba(26, 26, 26, 0.05)' : 'rgba(255, 255, 255, 0.06)') : 'transparent'; }}
        >
          <ChevronRight size={16} color={isLightMode ? '#1a1a1a' : '#ffffff'} />
        </div>
      </div>
    );
  };

  const calculateMonthlyIncome = (income, frequency) => {
    if (!income) return null;
    
    switch(frequency?.toLowerCase()) {
      case 'weekly':
        return Math.round(income * 4.33);
      case 'bi-weekly':
      case 'biweekly':
      case 'bi weekly':
        return Math.round(income * 2.17);
      case 'monthly':
      default:
        return income;
    }
  };

  const formatLivingSituation = (situation) => {
    const mapping = {
      'renting': 'Renting',
      'rent': 'Renting',
      'livingwithfamily': 'Family',
      'family': 'Family',
      'ownhome': 'Homeowner',
      'own': 'Homeowner'
    };
    return mapping[situation] || situation;
  };

  const formatPref = (val) => {
    if (val === 'yes') return 'Required';
    if (val === 'no') return 'No';
    return 'Any';
  };

  const formatAmenity = (amenityId) => {
    const mapping = {
      'hardwood': { emoji: '🪵', label: 'Hardwood Floors' },
      'aircon': { emoji: '❄️', label: 'Central Air/AC' },
      'garage': { emoji: '🚗', label: 'Garage' },
      'appliances': { emoji: '✨', label: 'Stainless Steel' },
      'view': { emoji: '🏔️', label: 'Scenic Views' },
      'pool': { emoji: '🏊', label: 'Swimming Pool' },
      'fireplace': { emoji: '🔥', label: 'Fireplace' },
      'balcony': { emoji: '🌅', label: 'Balcony/Patio' },
      'garden': { emoji: '🌸', label: 'Garden/Yard' },
      'walkincloset': { emoji: '👗', label: 'Walk-in Closet' },
      'security': { emoji: '🔒', label: 'Security System' },
      'gym': { emoji: '💪', label: 'Home Gym' },
      'office': { emoji: '💻', label: 'Home Office' }
    };
    return mapping[amenityId] || { emoji: '', label: amenityId };
  };

  const getLauftQDisplay = (lauftqType) => {
    if (!lauftqType) return 'Not Taken';
    return lauftqType;
  };

  // Get styles based on current theme
  const styles = getStyles(isLightMode);

  return createPortal(
    <>
      <style>{`
        /* Action buttons (MESSAGE / MATCHES) — on dark gradient chips, stay white */
        .buyer-modal-action-icon svg,
        .buyer-modal-action-icon svg line,
        .buyer-modal-action-icon svg polyline,
        .buyer-modal-action-icon svg path,
        .buyer-modal-action-icon svg circle,
        .buyer-modal-action-icon svg rect {
          stroke: #ffffff !important;
          color: #ffffff !important;
        }
        .buyer-modal-action-text {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
        /* Contact icons (Phone / Email / Location) — on dark gradient chips, stay white */
        .buyer-modal-contact-icon svg,
        .buyer-modal-contact-icon svg line,
        .buyer-modal-contact-icon svg polyline,
        .buyer-modal-contact-icon svg path,
        .buyer-modal-contact-icon svg circle,
        .buyer-modal-contact-icon svg rect {
          stroke: #ffffff !important;
          color: #ffffff !important;
        }
        .buyer-modal-tab,
        .buyer-modal-tab:focus,
        .buyer-modal-tab:focus-visible,
        .buyer-modal-tab:focus-within,
        .buyer-modal-tab:active,
        .buyer-modal-tab:hover,
        button.buyer-modal-tab,
        button.buyer-modal-tab:focus,
        button.buyer-modal-tab:focus-visible {
          outline: none !important;
          outline-width: 0 !important;
          outline-style: none !important;
          box-shadow: none !important;
          -webkit-tap-highlight-color: transparent !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
        }
        .buyer-modal-page-active span {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
      `}</style>
      <div 
        className={lightMode ? 'light-mode' : ''} 
        style={styles.overlay} 
        onClick={onClose}
      >
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={styles.header}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: isLightMode ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.15)',
                border: isLightMode ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid rgba(99, 102, 241, 0.4)',
                padding: '6px 12px',
                borderRadius: '8px',
              }}>
                <span style={{ fontSize: '1rem' }}>
                  {tierEmoji[buyer.leadTier?.toLowerCase()] || '❓'}
                </span>
                <span style={{
                  color: '#6366f1',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {buyer.leadTier || 'Unassigned'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={onClose} style={styles.closeButton}>
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Content */}
          {/* TWO COLUMN LAYOUT */}
          <div className="bdm-content" style={{ display: 'flex', gap: '24px', padding: '24px', minHeight: 'calc(95vh - 100px)' }}>
            
            {/* LEFT COLUMN - Quick Info (35%) */}
            <div className="bdm-left" style={{ width: '35%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* User Name Header */}
              <div style={{ paddingBottom: '20px', borderBottom: isLightMode ? '2px solid rgba(99, 102, 241, 0.2)' : '2px solid rgba(99, 102, 241, 0.3)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Avatar (wrapper carries the bot badge — .avatar itself clips on overflow) */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: (buyer.profileImage && buyer.profileImage.length > 5) ? 'none' : 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                    border: isLightMode ? '2px solid rgba(99, 102, 241, 0.3)' : '2px solid rgba(99, 102, 241, 0.4)',
                  }}>
                    {(buyer.profileImage && buyer.profileImage.length > 5) ? (
                      <img 
                        src={buyer.profileImage} 
                        alt={buyer.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.style.background = 'linear-gradient(135deg, #4b5563 0%, #374151 100%)';
                          e.target.insertAdjacentHTML('afterend', '<svg viewBox="0 0 24 24" fill="none" style="width:60%;height:60%"><circle cx="12" cy="8" r="4" fill="rgba(255,255,255,0.5)"/><path d="M4 20c0-3.3 2.7-6 6-6h4c3.3 0 6 2.7 6 6" fill="rgba(255,255,255,0.5)"/></svg>');
                        }}
                      />
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" style={{ width: '60%', height: '60%' }}>
                        <circle cx="12" cy="8" r="4" fill="rgba(255,255,255,0.5)" />
                        <path d="M4 20c0-3.3 2.7-6 6-6h4c3.3 0 6 2.7 6 6" fill="rgba(255,255,255,0.5)" />
                      </svg>
                    )}
                  </div>
                  {buyer.hasBotChat && (
                    <span
                      title="Has chatted with the Lauft assistant"
                      style={{
                        position: 'absolute',
                        bottom: '-2px',
                        right: '-2px',
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: '#ece9fb',
                        border: '2px solid #fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 3,
                        boxSizing: 'border-box',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                      }}
                    >
                      <img
                        src={darkIcon}
                        alt="Bot"
                        style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                      />
                    </span>
                  )}
                  </div>
                  <div>
                    <h1 style={{
                      fontSize: '2rem',
                      fontWeight: '700',
                      margin: 0,
                      color: isLightMode ? '#1a1a1a' : '#ffffff',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2,
                      textAlign: 'left'
                    }}>
                      {buyer.name}
                    </h1>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px', textAlign: 'left' }}>
                  {buyer.lauftqType && (
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#818cf8',
                      letterSpacing: '0.02em',
                      textAlign: 'left',
                    }}>
                      {buyer.lauftqType}
                    </div>
                  )}
                  <div style={{
                    fontSize: '0.8rem',
                    color: isLightMode ? 'rgba(26, 26, 26, 0.45)' : 'rgba(255, 255, 255, 0.4)',
                    fontWeight: '400',
                    textAlign: 'left',
                  }}>
                    {buyer.createdAt ? `User since ${new Date(buyer.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : 'New user'}
                  </div>
                </div>
              </div>

              {/* Contact Action Pills */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {buyer.phone && (
                  <a
                    href={`tel:${buyer.phone}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                      border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <div className="buyer-modal-contact-icon" style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Phone size={20} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '0.7rem',
                        color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontWeight: '600',
                        marginBottom: '2px'
                      }}>Phone</div>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: isLightMode ? '#1a1a1a' : '#ffffff'
                      }}>
                        {formatPhoneNumber(buyer.phone)}
                      </div>
                    </div>
                  </a>
                )}

                {buyer.email && (
                  <a
                    href={`mailto:${buyer.email}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                      border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <div className="buyer-modal-contact-icon" style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Mail size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '0.7rem',
                        color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontWeight: '600',
                        marginBottom: '2px'
                      }}>Email</div>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: isLightMode ? '#1a1a1a' : '#ffffff',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {buyer.email}
                      </div>
                    </div>
                  </a>
                )}

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                  border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}>
                  <div className="buyer-modal-contact-icon" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <MapPin size={20} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.7rem',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: '600',
                      marginBottom: '2px'
                    }}>Location</div>
                    <div style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: isLightMode ? '#1a1a1a' : '#ffffff',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {buyer.location || 'Not provided'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Precaution Mode Release Banner — only shown when agent info is being held from the user */}
              {!agentInfoReleased && (
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: '#dbeafe',
                    border: '1px solid #93c5fd',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: '#3b82f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Lock size={18} color="#ffffff" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        color: '#1e40af',
                        marginBottom: '4px'
                      }}>
                        Info held — Precaution mode
                      </div>
                      <div style={{
                        fontSize: '0.8rem',
                        color: '#1e3a8a',
                        lineHeight: '1.45'
                      }}>
                        When you are ready, click the button below to send the user the introduction email and push notification informing them that you have been assigned as their agent.
                      </div>
                    </div>
                  </div>

                  {releaseError && (
                    <div style={{
                      fontSize: '0.78rem',
                      color: '#991b1b',
                      backgroundColor: '#fee2e2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      padding: '8px 10px'
                    }}>
                      {releaseError}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleReleaseAgentInfo}
                    disabled={isReleasingInfo}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      backgroundColor: isReleasingInfo ? '#60a5fa' : '#3b82f6',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: isReleasingInfo ? 'wait' : 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      if (!isReleasingInfo) {
                        e.currentTarget.style.backgroundColor = '#2563eb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isReleasingInfo) {
                        e.currentTarget.style.backgroundColor = '#3b82f6';
                      }
                    }}
                  >
                    <Send size={16} />
                    {isReleasingInfo ? 'Sending…' : 'Send My Info to User'}
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <button
                  onClick={() => {
                    if (onMessage) {
                      onMessage(buyer);
                    } else {
                      console.log('No onMessage handler provided for', buyer._id);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px 24px',
                    background: 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(55, 65, 81, 0.3)',
                    color: '#ffffff',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(55, 65, 81, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(55, 65, 81, 0.3)';
                  }}
                >
                  <div className="buyer-modal-action-icon" style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    padding: '12px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MessageCircle size={28} strokeWidth={2.5} />
                  </div>
                  <span className="buyer-modal-action-text" style={{ flex: 1, textAlign: 'left' }}>Message</span>
                </button>

                {onCurateMatch && (
                <button
                  onClick={() => onCurateMatch(buyer)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    padding: '20px 24px',
                    background: 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(55, 65, 81, 0.3)',
                    color: '#ffffff',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(55, 65, 81, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(55, 65, 81, 0.3)';
                  }}
                >
                  <div className="buyer-modal-action-icon" style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    padding: '12px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Inbox size={28} strokeWidth={2.5} />
                  </div>
                  <span className="buyer-modal-action-text" style={{ flex: 1, textAlign: 'left' }}>Matches</span>
                </button>
                )}
              </div>

              {/* Meta Info at Bottom */}
              <div style={{
                marginTop: 'auto',
                padding: '16px',
                backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.03)' : 'rgba(255, 255, 255, 0.03)',
                borderRadius: '8px',
                fontSize: '0.8rem',
                color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
              }}>
                <div style={{ marginBottom: '6px' }}>
                  <strong>Registered:</strong> {formatDate(buyer.createdAt)}
                </div>
                <div>
                  <strong>Last Active:</strong> {buyer.lastSwipedAt ? formatDate(buyer.lastSwipedAt) : 'N/A'}
                </div>
              </div>

            </div>

            {/* RIGHT COLUMN - Detailed Info (65%) */}
            <div className="bdm-right" style={{ 
              width: '65%',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              backgroundColor: isLightMode ? '#f5f5f5' : 'rgba(0, 0, 0, 0.2)',
              padding: '24px',
              borderRadius: '12px',
              height: '100%',
              overflowY: 'auto'
            }}>
              
              {/* Tab Navigation */}
              <div style={{
                display: 'flex',
                gap: '16px',
                borderBottom: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                {[
                  { key: 'overview', label: 'Overview' },
                  { key: 'liked', label: `Liked (${buyer.likes?.length || 0})` },
                  { key: 'passed', label: `Passed (${buyer.dislikes?.length || 0})` },
                  { key: 'questions', label: `Questions (${userQuestions.length})` },
                ].map((tab) => (
                  <div
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'transparent',
                      color: activeTab === tab.key 
                        ? (isLightMode ? '#1a1a1a' : '#ffffff')
                        : (isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'),
                      borderBottom: activeTab === tab.key 
                        ? (isLightMode ? '2px solid #1a1a1a' : '2px solid #ffffff')
                        : '2px solid transparent',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      transition: 'color 0.2s',
                      marginBottom: '-1px',
                      userSelect: 'none',
                    }}
                  >
                    {tab.label}
                  </div>
                ))}
              </div>

              {/* Tab Content - Overview */}
              {activeTab === 'overview' && (
              <>
              <div style={{
                backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.03)',
                border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  margin: '0 0 16px 0',
                  color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>Search Filters</h3>

                {/* ─── Accordion: Core Filters ─── */}
                <div
                  onClick={() => setOpenFilterSection(openFilterSection === 'core' ? null : 'core')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.07)' : 'rgba(255, 255, 255, 0.07)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.04)' : 'rgba(255, 255, 255, 0.04)'}
                >
                  <span style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px'
                  }}>Core Filters</span>
                  <ChevronDown
                    size={16}
                    color={isLightMode ? '#1a1a1a' : '#ffffff'}
                    style={{
                      transform: openFilterSection === 'core' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  />
                </div>

                {openFilterSection === 'core' && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    textAlign: 'center',
                    padding: '16px 4px 4px 4px'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Looking For</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff', textTransform: 'capitalize' }}>
                        {buyer.filterPreferences?.listingType || buyer.onboardingData?.housingChoice || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Price Range</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {buyer.filterPreferences?.priceRange
                          ? `${formatCurrency(buyer.filterPreferences.priceRange.min)} - ${buyer.filterPreferences.priceRange.max >= 999999999 ? 'Any' : formatCurrency(buyer.filterPreferences.priceRange.max)}`
                          : (buyer.minPrice ? `${formatCurrency(buyer.minPrice)} - ${formatCurrency(buyer.maxPrice)}` : 'N/A')}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Bedrooms</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {(buyer.filterPreferences?.bedrooms != null)
                          ? (buyer.filterPreferences.bedrooms === 0 ? 'Any' : `${buyer.filterPreferences.bedrooms}+`)
                          : (buyer.bedrooms || 'N/A')}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Bathrooms</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {(buyer.filterPreferences?.bathrooms != null)
                          ? (buyer.filterPreferences.bathrooms === 0 ? 'Any' : `${buyer.filterPreferences.bathrooms}+`)
                          : (buyer.bathrooms || 'N/A')}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Property Type</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {(buyer.filterPreferences?.propertyTypes?.length > 0)
                          ? buyer.filterPreferences.propertyTypes.join(', ')
                          : (buyer.propertyTypes?.join(', ') || 'Any')}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Location / Radius</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {buyer.filterPreferences?.location?.method === 'custom' && buyer.filterPreferences.location.customLocation
                          ? `${buyer.filterPreferences.location.customLocation} (${buyer.filterPreferences.location.radius || 10} mi)`
                          : `Current Location (${buyer.filterPreferences?.location?.radius || 10} mi)`}
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── Accordion: Home Features ─── */}
                <div
                  onClick={() => setOpenFilterSection(openFilterSection === 'home' ? null : 'home')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    marginTop: '10px',
                    backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.07)' : 'rgba(255, 255, 255, 0.07)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.04)' : 'rgba(255, 255, 255, 0.04)'}
                >
                  <span style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px'
                  }}>Home Features</span>
                  <ChevronDown
                    size={16}
                    color={isLightMode ? '#1a1a1a' : '#ffffff'}
                    style={{
                      transform: openFilterSection === 'home' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  />
                </div>

                {openFilterSection === 'home' && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    textAlign: 'center',
                    padding: '16px 4px 4px 4px'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Square Footage</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {(() => {
                          const min = buyer.filterPreferences?.homeFeatures?.minSqft;
                          const max = buyer.filterPreferences?.homeFeatures?.maxSqft;
                          if (!min && !max) return 'Any';
                          if (min && !max) return `${min.toLocaleString()}+ sqft`;
                          if (!min && max) return `Up to ${max.toLocaleString()} sqft`;
                          return `${min.toLocaleString()} - ${max.toLocaleString()} sqft`;
                        })()}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Year Built</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {buyer.filterPreferences?.homeFeatures?.yearBuilt || 'Any'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Garage</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {buyer.filterPreferences?.homeFeatures?.garage > 0
                          ? `${buyer.filterPreferences.homeFeatures.garage}+ car`
                          : 'Any'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Single Story</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {buyer.filterPreferences?.homeFeatures?.singleStory ? 'Required' : 'Any'}
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── Accordion: Advanced Filters ─── */}
                <div
                  onClick={() => setOpenFilterSection(openFilterSection === 'advanced' ? null : 'advanced')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    marginTop: '10px',
                    backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.07)' : 'rgba(255, 255, 255, 0.07)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.04)' : 'rgba(255, 255, 255, 0.04)'}
                >
                  <span style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px'
                  }}>Advanced Filters</span>
                  <ChevronDown
                    size={16}
                    color={isLightMode ? '#1a1a1a' : '#ffffff'}
                    style={{
                      transform: openFilterSection === 'advanced' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  />
                </div>

                {openFilterSection === 'advanced' && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    textAlign: 'center',
                    padding: '16px 4px 4px 4px'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>HOA</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {formatPref(buyer.filterPreferences?.advancedFilters?.hoaPreference)}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Lot Size</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {(() => {
                          const min = buyer.filterPreferences?.advancedFilters?.minLotSize;
                          const max = buyer.filterPreferences?.advancedFilters?.maxLotSize;
                          if (!min && !max) return 'Any';
                          if (min && !max) return `${min.toLocaleString()}+ sqft`;
                          if (!min && max) return `Up to ${max.toLocaleString()} sqft`;
                          return `${min.toLocaleString()} - ${max.toLocaleString()} sqft`;
                        })()}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Pool</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {formatPref(buyer.filterPreferences?.advancedFilters?.poolPreference)}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Basement</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {formatPref(buyer.filterPreferences?.advancedFilters?.basementPreference)}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Fireplace</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {formatPref(buyer.filterPreferences?.advancedFilters?.fireplacePreference)}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Min School Rating</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                        {buyer.filterPreferences?.advancedFilters?.minSchoolRating > 0
                          ? `${buyer.filterPreferences.advancedFilters.minSchoolRating}+ / 10`
                          : 'Any'}
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── Accordion: Rental Preferences (only when listingType === 'rent') ─── */}
                {buyer.filterPreferences?.listingType === 'rent' && (
                  <>
                    <div
                      onClick={() => setOpenFilterSection(openFilterSection === 'rental' ? null : 'rental')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 14px',
                        marginTop: '10px',
                        backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.07)' : 'rgba(255, 255, 255, 0.07)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(26, 26, 26, 0.04)' : 'rgba(255, 255, 255, 0.04)'}
                    >
                      <span style={{
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: isLightMode ? '#1a1a1a' : '#ffffff',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px'
                      }}>Rental Preferences</span>
                      <ChevronDown
                        size={16}
                        color={isLightMode ? '#1a1a1a' : '#ffffff'}
                        style={{
                          transform: openFilterSection === 'rental' ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                      />
                    </div>

                    {openFilterSection === 'rental' && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        textAlign: 'center',
                        padding: '16px 4px 4px 4px'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Dogs Allowed</div>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                            {buyer.filterPreferences?.rentalDetails?.allowsDogs ? 'Required' : 'Not required'}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Cats Allowed</div>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                            {buyer.filterPreferences?.rentalDetails?.allowsCats ? 'Required' : 'Not required'}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Parking</div>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff', textTransform: 'capitalize' }}>
                            {buyer.filterPreferences?.rentalDetails?.parkingTypes?.length > 0
                              ? buyer.filterPreferences.rentalDetails.parkingTypes.join(', ')
                              : 'Any'}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>Laundry</div>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: isLightMode ? '#1a1a1a' : '#ffffff' }}>
                            {buyer.filterPreferences?.rentalDetails?.laundryTypes?.length > 0
                              ? buyer.filterPreferences.rentalDetails.laundryTypes.map(t => t === 'inUnit' ? 'In-Unit' : t === 'onSite' ? 'On-Site' : 'None').join(', ')
                              : 'Any'}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ─── Preferred Amenities (chips, always visible if any) ─── */}
                {buyer.filterPreferences?.preferredAmenities?.length > 0 && (
                  <div style={{
                    marginTop: '20px',
                    paddingTop: '20px',
                    borderTop: isLightMode ? '1px solid rgba(26, 26, 26, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      marginBottom: '8px',
                      textAlign: 'left'
                    }}>Preferred Amenities</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-start' }}>
                      {buyer.filterPreferences.preferredAmenities.map((amenity, idx) => {
                        const { emoji, label } = formatAmenity(amenity);
                        return (
                          <span
                            key={idx}
                            style={{
                              fontSize: '0.8rem',
                              padding: '6px 12px',
                              backgroundColor: isLightMode ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.15)',
                              borderRadius: '6px',
                              color: isLightMode ? '#1a1a1a' : '#ffffff',
                              border: isLightMode ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid rgba(99, 102, 241, 0.3)'
                            }}
                          >
                            {emoji} {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Onboarding / Financial Info */}
                <h3 style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  margin: '24px 0 16px 0',
                  color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  paddingTop: '20px',
                  borderTop: isLightMode ? '1px solid rgba(26, 26, 26, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)',
                  textAlign: 'center'
                }}>User Profile</h3>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  textAlign: 'center'
                }}>
                  <div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      marginBottom: '4px'
                    }}>Monthly Income</div>
                    <div style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: isLightMode ? '#1a1a1a' : '#ffffff'
                    }}>
                      {(buyer.onboardingData?.monthlyIncome || buyer.monthlyIncome)
                        ? formatCurrency(calculateMonthlyIncome(
                            buyer.onboardingData?.monthlyIncome || buyer.monthlyIncome, 
                            buyer.onboardingData?.incomeFrequency || buyer.incomeFrequency
                          ))
                        : 'N/A'}
                      {(buyer.onboardingData?.incomeFrequency || buyer.incomeFrequency) && (buyer.onboardingData?.monthlyIncome || buyer.monthlyIncome) && (
                        <span style={{ fontSize: '0.75rem', color: isLightMode ? 'rgba(26, 26, 26, 0.4)' : 'rgba(255, 255, 255, 0.4)', marginLeft: '4px' }}>
                          /mo
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      marginBottom: '4px'
                    }}>Credit Score</div>
                    <div style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: isLightMode ? '#1a1a1a' : '#ffffff'
                    }}>
                      {(buyer.onboardingData?.creditScore || buyer.creditScore) || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      marginBottom: '4px'
                    }}>Current Living</div>
                    <div style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: isLightMode ? '#1a1a1a' : '#ffffff'
                    }}>
                      {formatLivingSituation(buyer.onboardingData?.livingSituation || buyer.livingSituation)}
                    </div>
                  </div>

                  <div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      marginBottom: '4px'
                    }}>Current Expense</div>
                    <div style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: isLightMode ? '#1a1a1a' : '#ffffff'
                    }}>
                      {(buyer.onboardingData?.currentExpense || buyer.currentExpense)
                        ? `${formatCurrency(buyer.onboardingData?.currentExpense || buyer.currentExpense)}/mo`
                        : 'N/A'}
                    </div>
                  </div>

                  <div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      marginBottom: '4px'
                    }}>Dream Budget</div>
                    <div style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: isLightMode ? '#1a1a1a' : '#ffffff'
                    }}>
                      {(buyer.onboardingData?.budgetExpense || buyer.budgetExpense)
                        ? `${formatCurrency(buyer.onboardingData?.budgetExpense || buyer.budgetExpense)}/mo`
                        : 'N/A'}
                    </div>
                  </div>

                  <div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      marginBottom: '4px'
                    }}>Last Swipe</div>
                    <div style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: isLightMode ? '#1a1a1a' : '#ffffff'
                    }}>
                      {buyer.lastSwipedAt ? formatDate(buyer.lastSwipedAt) : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Stats - 3 Cards Side by Side */}
              <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                {/* Likes Card */}
                <div style={{
                  flex: 1,
                  backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                  border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Heart size={14} color="#ef4444" fill="#ef4444" />
                    Liked
                  </div>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    lineHeight: 1,
                    marginBottom: '6px'
                  }}>
                    {buyer.likes?.length || 0}
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
                  }}>
                    {buyer.likes?.length === 1 ? 'Property' : 'Properties'}
                  </div>
                </div>

                {/* Passes Card */}
                <div style={{
                  flex: 1,
                  backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                  border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <XCircle size={14} color="#94a3b8" />
                    Passed
                  </div>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    lineHeight: 1,
                    marginBottom: '6px'
                  }}>
                    {buyer.dislikes?.length || 0}
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
                  }}>
                    {buyer.dislikes?.length === 1 ? 'Property' : 'Properties'}
                  </div>
                </div>

                {/* Showings Card */}
                <div style={{
                  flex: 1,
                  backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                  border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Calendar size={14} color="#3b82f6" />
                    Showings
                  </div>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    lineHeight: 1,
                    marginBottom: '6px'
                  }}>
                    {showingsCount}
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
                  }}>
                    Scheduled
                  </div>
                </div>
              </div>

              {/* Lauft Assistant Conversation (read-only) */}
              <div style={{
                backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.03)',
                border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <img src={darkIcon} alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} />
                  <span style={{
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Lauft Assistant Conversation
                  </span>
                </div>

                {!showBotTranscript ? (
                  <button
                    onClick={fetchBotConversation}
                    style={styles.viewBotButton}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(37, 99, 235, 0.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                  >
                    View assistant chat history
                  </button>
                ) : botLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6, fontSize: '13px' }}>
                    Loading conversation…
                  </div>
                ) : botMessages.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6, fontSize: '13px' }}>
                    This user hasn't chatted with the assistant yet.
                  </div>
                ) : (
                  <div style={styles.botTranscript}>
                    {botMessages.map((msg, idx) => {
                      const isBot = msg.sender == null; // bot messages are stored with sender: null
                      return (
                        <div
                          key={msg._id || idx}
                          style={{ ...styles.botRow, justifyContent: isBot ? 'flex-start' : 'flex-end' }}
                        >
                          <div style={isBot ? styles.botBubble : styles.botUserBubble}>
                            <div style={{ ...styles.botBubbleText, ...(isBot ? {} : styles.botUserText) }}>
                              {msg.content}
                            </div>
                            <div style={{ ...styles.botBubbleTime, ...(isBot ? {} : { color: '#fff' }) }}>
                              {msg.createdAt
                                ? new Date(msg.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </>
              )}

              {/* Tab Content - Liked Properties */}
              {activeTab === 'liked' && buyer.likes && buyer.likes.length > 0 && (
                <div>
                  <div 
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '20px 24px',
                      borderRadius: '12px',
                      background: isLightMode 
                        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.08) 100%)',
                      border: isLightMode ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(239, 68, 68, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      userSelect: 'none',
                    }}
                    onClick={() => setLikesExpanded(!likesExpanded)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isLightMode 
                        ? '0 4px 12px rgba(239, 68, 68, 0.15)' 
                        : '0 4px 12px rgba(239, 68, 68, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{
                        backgroundColor: isLightMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.25)',
                        padding: '12px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Heart 
                          size={24} 
                          fill="#ef4444"
                          color="#ef4444"
                        />
                      </div>
                      <div>
                        <h3 style={{
                          margin: 0,
                          fontSize: '1.25rem',
                          fontWeight: '600',
                          color: isLightMode ? '#1a1a1a' : '#ffffff',
                          letterSpacing: '-0.01em'
                        }}>Liked Properties</h3>
                        <p style={{
                          margin: '4px 0 0 0',
                          fontSize: '0.875rem',
                          color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                          fontWeight: '500'
                        }}>{buyer.likes.length} {buyer.likes.length === 1 ? 'property' : 'properties'}</p>
                      </div>
                    </div>
                    <ChevronDown 
                      size={24} 
                      style={{ 
                        transition: 'transform 0.3s',
                        transform: likesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
                      }}
                    />
                  </div>
                  
                  {likesExpanded && (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                        gap: '16px',
                      }}>
                        {[...buyer.likes].reverse().slice(likesPage * 6, (likesPage + 1) * 6).map((property, index) => {
                          if (typeof property === 'string' || !property.address) {
                            return null;
                          }
                          
                          return (
                            <div
                              key={property._id || index}
                              style={{
                                backgroundColor: isLightMode ? '#f9f8f5' : 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                                transition: 'transform 0.2s, border-color 0.2s',
                                cursor: 'pointer',
                              }}
                              onClick={() => handlePropertyClick(property)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = isLightMode ? 'rgba(26, 26, 26, 0.1)' : 'rgba(255, 255, 255, 0.1)';
                              }}
                            >
                              <div style={{ position: 'relative' }}>
                                {(property.mainImage || (property.images && property.images.length > 0)) ? (
                                  <img
                                    src={property.mainImage || property.images[0]}
                                    alt={property.address || 'Property'}
                                    style={{
                                      width: '100%',
                                      height: '150px',
                                      objectFit: 'cover',
                                      display: 'block',
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '100%',
                                    height: '150px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                                    color: isLightMode ? 'rgba(26, 26, 26, 0.3)' : 'rgba(255, 255, 255, 0.3)'
                                  }}>
                                    No Image
                                  </div>
                                )}
                                {property.propertyStatus === 'Off Market' && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    left: '8px',
                                    padding: '3px 8px',
                                    background: 'rgba(17, 24, 39, 0.78)',
                                    backdropFilter: 'blur(4px)',
                                    WebkitBackdropFilter: 'blur(4px)',
                                    color: '#ffffff',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    borderRadius: '4px',
                                    lineHeight: 1.4,
                                  }}>
                                    Status Change
                                  </div>
                                )}
                              </div>
                              <div style={{ padding: '12px' }}>
                                <div style={{
                                  color: isLightMode ? '#1a1a1a' : '#ffffff',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  marginBottom: '4px',
                                }}>
                                  {property.address || 'Address not available'}
                                </div>
                                {property.csz && (
                                  <div style={{
                                    color: isLightMode ? '#6b7280' : 'rgba(255, 255, 255, 0.5)',
                                    fontSize: '0.78rem',
                                    marginBottom: '6px',
                                  }}>
                                    {property.csz}
                                  </div>
                                )}
                                <div style={{
                                  color: isLightMode ? 'rgba(26, 26, 26, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                                  fontSize: '0.85rem',
                                }}>
                                  {property.price || formatCurrency(property.numericPrice)}
                                </div>
                                <div style={{
                                  color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                  fontSize: '0.8rem',
                                  marginTop: '4px',
                                }}>
                                  {property.bedrooms || 0} bed • {property.bathrooms || 0} bath
                                  {property.sqft && ` • ${property.sqft.toLocaleString()} sqft`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {buyer.likes.length > 6 && renderPagination(likesPage, buyer.likes.length, 6, setLikesPage)}
                    </div>
                  )}
                </div>
              )}

              {/* Tab Content - Passed Properties */}
              {activeTab === 'passed' && buyer.dislikes && buyer.dislikes.length > 0 && (
                <div>
                  <div 
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '20px 24px',
                      borderRadius: '12px',
                      background: isLightMode 
                        ? 'linear-gradient(135deg, rgba(148, 163, 184, 0.08) 0%, rgba(100, 116, 139, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(148, 163, 184, 0.15) 0%, rgba(100, 116, 139, 0.08) 100%)',
                      border: isLightMode ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(148, 163, 184, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      userSelect: 'none',
                    }}
                    onClick={() => setPassedExpanded(!passedExpanded)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = isLightMode 
                        ? '0 4px 12px rgba(148, 163, 184, 0.15)' 
                        : '0 4px 12px rgba(148, 163, 184, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{
                        backgroundColor: isLightMode ? 'rgba(148, 163, 184, 0.15)' : 'rgba(148, 163, 184, 0.25)',
                        padding: '12px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <XCircle 
                          size={24} 
                          color="#94a3b8"
                        />
                      </div>
                      <div>
                        <h3 style={{
                          margin: 0,
                          fontSize: '1.25rem',
                          fontWeight: '600',
                          color: isLightMode ? '#1a1a1a' : '#ffffff',
                          letterSpacing: '-0.01em'
                        }}>Passed Properties</h3>
                        <p style={{
                          margin: '4px 0 0 0',
                          fontSize: '0.875rem',
                          color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                          fontWeight: '500'
                        }}>{buyer.dislikes.length} {buyer.dislikes.length === 1 ? 'property' : 'properties'}</p>
                      </div>
                    </div>
                    <ChevronDown 
                      size={24} 
                      style={{ 
                        transition: 'transform 0.3s',
                        transform: passedExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
                      }}
                    />
                  </div>
                  
                  {passedExpanded && (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                        gap: '16px',
                      }}>
                        {[...buyer.dislikes].reverse().slice(dislikesPage * 6, (dislikesPage + 1) * 6).map((property, index) => {
                          if (typeof property === 'string' || !property.address) {
                            return null;
                          }
                          
                          return (
                            <div
                              key={property._id || index}
                              style={{
                                backgroundColor: isLightMode ? '#f9f8f5' : 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                                transition: 'transform 0.2s, border-color 0.2s',
                                cursor: 'pointer',
                              }}
                              onClick={() => handlePropertyClick(property)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = isLightMode ? 'rgba(26, 26, 26, 0.1)' : 'rgba(255, 255, 255, 0.1)';
                              }}
                            >
                              <div style={{ position: 'relative' }}>
                                {(property.mainImage || (property.images && property.images.length > 0)) ? (
                                  <img
                                    src={property.mainImage || property.images[0]}
                                    alt={property.address || 'Property'}
                                    style={{
                                      width: '100%',
                                      height: '150px',
                                      objectFit: 'cover',
                                      display: 'block',
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '100%',
                                    height: '150px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                                    color: isLightMode ? 'rgba(26, 26, 26, 0.3)' : 'rgba(255, 255, 255, 0.3)'
                                  }}>
                                    No Image
                                  </div>
                                )}
                                {property.propertyStatus === 'Off Market' && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    left: '8px',
                                    padding: '3px 8px',
                                    background: 'rgba(17, 24, 39, 0.78)',
                                    backdropFilter: 'blur(4px)',
                                    WebkitBackdropFilter: 'blur(4px)',
                                    color: '#ffffff',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    borderRadius: '4px',
                                    lineHeight: 1.4,
                                  }}>
                                    Status Change
                                  </div>
                                )}
                              </div>
                              <div style={{ padding: '12px' }}>
                                <div style={{
                                  color: isLightMode ? '#1a1a1a' : '#ffffff',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  marginBottom: '4px',
                                }}>
                                  {property.address || 'Address not available'}
                                </div>
                                {property.csz && (
                                  <div style={{
                                    color: isLightMode ? '#6b7280' : 'rgba(255, 255, 255, 0.5)',
                                    fontSize: '0.78rem',
                                    marginBottom: '6px',
                                  }}>
                                    {property.csz}
                                  </div>
                                )}
                                <div style={{
                                  color: isLightMode ? 'rgba(26, 26, 26, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                                  fontSize: '0.85rem',
                                }}>
                                  {property.price || formatCurrency(property.numericPrice)}
                                </div>
                                <div style={{
                                  color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                                  fontSize: '0.8rem',
                                  marginTop: '4px',
                                }}>
                                  {property.bedrooms || 0} bed • {property.bathrooms || 0} bath
                                  {property.sqft && ` • ${property.sqft.toLocaleString()} sqft`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {buyer.dislikes.length > 6 && renderPagination(dislikesPage, buyer.dislikes.length, 6, setDislikesPage)}
                    </div>
                  )}
                </div>
              )}

              {/* Tab Content - Questions */}
              {activeTab === 'questions' && (
                <div>
                  {userQuestions.length === 0 ? (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
                    }}>
                      <p>No questions asked yet.</p>
                    </div>
                  ) : (
                    <>
                      {/* Accordion Header */}
                      <div 
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '20px 24px',
                          borderRadius: '12px',
                          background: isLightMode 
                            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(129, 140, 248, 0.05) 100%)'
                            : 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(129, 140, 248, 0.08) 100%)',
                          border: isLightMode ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid rgba(99, 102, 241, 0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          userSelect: 'none',
                        }}
                        onClick={() => setQuestionsExpanded(!questionsExpanded)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = isLightMode 
                            ? '0 4px 12px rgba(99, 102, 241, 0.15)' 
                            : '0 4px 12px rgba(99, 102, 241, 0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div style={{
                            backgroundColor: isLightMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.25)',
                            padding: '12px',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <HelpCircle 
                              size={24} 
                              color="#6366f1"
                            />
                          </div>
                          <div>
                            <h3 style={{
                              margin: 0,
                              fontSize: '1.25rem',
                              fontWeight: '600',
                              color: isLightMode ? '#1a1a1a' : '#ffffff',
                              letterSpacing: '-0.01em'
                            }}>Questions Asked</h3>
                            <p style={{
                              margin: '4px 0 0 0',
                              fontSize: '0.875rem',
                              color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                              fontWeight: '500'
                            }}>{userQuestions.length} {userQuestions.length === 1 ? 'question' : 'questions'}</p>
                          </div>
                        </div>
                        <ChevronDown 
                          size={24} 
                          style={{ 
                            transition: 'transform 0.3s',
                            transform: questionsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
                          }}
                        />
                      </div>

                      {/* Expanded Content */}
                      {questionsExpanded && (
                        <div style={{ marginTop: '16px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {userQuestions.slice(questionsPage * 3, (questionsPage + 1) * 3).map((q, idx) => (
                              <div 
                                key={q._id || idx}
                                style={{
                                  backgroundColor: isLightMode ? '#f9f8f5' : 'rgba(255, 255, 255, 0.05)',
                                  border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                                  borderRadius: '10px',
                                  overflow: 'hidden',
                                  transition: 'transform 0.2s, border-color 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = isLightMode ? 'rgba(26, 26, 26, 0.1)' : 'rgba(255, 255, 255, 0.1)';
                                }}
                              >
                                {/* Property row */}
                                {q.property && (
                                  <div 
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '14px',
                                      padding: '14px 16px',
                                      borderBottom: isLightMode ? '1px solid rgba(26, 26, 26, 0.06)' : '1px solid rgba(255, 255, 255, 0.06)',
                                      cursor: 'pointer',
                                      transition: 'opacity 0.2s'
                                    }}
                                    onClick={() => handlePropertyClick(q.property)}
                                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                                  >
                                    <div style={{
                                      width: '48px',
                                      height: '48px',
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                      flexShrink: 0,
                                      backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                                    }}>
                                      {q.property.mainImage ? (
                                        <img src={q.property.mainImage} alt={q.property.address}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                      ) : (
                                        <div style={{
                                          width: '100%', height: '100%',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                          color: isLightMode ? 'rgba(26, 26, 26, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                                          fontSize: '0.65rem'
                                        }}>No img</div>
                                      )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      {q.property.price && (
                                        <div style={{
                                          fontSize: '0.9rem', fontWeight: '700',
                                          color: isLightMode ? '#1a1a1a' : '#ffffff',
                                          marginBottom: '1px'
                                        }}>{q.property.price}</div>
                                      )}
                                      <div style={{
                                        fontSize: '0.8rem',
                                        color: isLightMode ? 'rgba(26, 26, 26, 0.55)' : 'rgba(255, 255, 255, 0.5)',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                      }}>{q.property.address || 'Unknown Property'}</div>
                                    </div>
                                  </div>
                                )}

                                {/* Question + Status */}
                                <div style={{ padding: '14px 16px' }}>
                                  <div style={{
                                    fontSize: '0.9rem',
                                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                                    lineHeight: 1.5,
                                    marginBottom: '12px'
                                  }}>
                                    "{q.question}"
                                  </div>

                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}>
                                    {q.status === 'answered' ? (
                                      <span style={{
                                        fontSize: '0.75rem', padding: '4px 10px', borderRadius: '6px',
                                        fontWeight: '600',
                                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                        color: '#10b981',
                                        border: '1px solid rgba(16, 185, 129, 0.3)'
                                      }}>✓ Responded</span>
                                    ) : (
                                      <span style={{
                                        fontSize: '0.75rem', padding: '4px 10px', borderRadius: '6px',
                                        fontWeight: '600',
                                        backgroundColor: q.status === 'in-progress' 
                                          ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                        color: q.status === 'in-progress' ? '#f59e0b' : '#ef4444',
                                        border: q.status === 'in-progress'
                                          ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)'
                                      }}>
                                        {q.status === 'in-progress' ? 'In Progress' : 'Awaiting Response'}
                                      </span>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <span style={{
                                        fontSize: '0.7rem',
                                        color: isLightMode ? 'rgba(26, 26, 26, 0.4)' : 'rgba(255, 255, 255, 0.35)',
                                      }}>
                                        {q.status === 'answered' && q.response?.respondedAt
                                          ? `Completed ${formatDate(q.response.respondedAt)}`
                                          : `Asked ${formatDate(q.createdAt)}`}
                                      </span>
                                      {q.status !== 'answered' && (
                                        <div
                                          onClick={() => markQuestionComplete(q._id)}
                                          style={{
                                            padding: '4px 10px', borderRadius: '6px',
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: '#ffffff', fontSize: '0.7rem', fontWeight: '600',
                                            cursor: isMarkingComplete === q._id ? 'not-allowed' : 'pointer',
                                            opacity: isMarkingComplete === q._id ? 0.5 : 1,
                                            transition: 'all 0.2s', userSelect: 'none',
                                          }}
                                          onMouseEnter={(e) => {
                                            if (isMarkingComplete !== q._id) {
                                              e.currentTarget.style.transform = 'translateY(-1px)';
                                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                          }}
                                        >
                                          {isMarkingComplete === q._id ? 'Marking...' : 'Mark Complete'}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Pagination */}
                          {renderPagination(questionsPage, userQuestions.length, 3, setQuestionsPage)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {showPropertyModal && selectedProperty && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10001 }}>
          <PropertyDetailsModal
            property={selectedProperty}
            isOpen={showPropertyModal}
            onClose={() => {
              setShowPropertyModal(false);
              setSelectedProperty(null);
            }}
          />
        </div>
      )}
    </>,
    document.body
  );
};

const getStyles = (isLightMode) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    background: '#ffffff',
    borderRadius: '16px',
    width: '85vw',
    height: '95vh',
    maxWidth: '1600px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px',
    borderBottom: '1px solid #f0f0f0',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: '700',
    margin: 0,
    color: '#111',
    textAlign: 'left',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '4px 0 0 0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: 0,
    transition: 'color 0.12s',
  },
  content: {
    padding: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  infoIcon: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
    marginTop: '2px',
  },
  infoLabel: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.75rem',
    margin: '0 0 4px 0',
  },
  infoValue: {
    color: isLightMode ? '#1a1a1a' : '#ffffff',
    fontSize: '0.875rem',
    margin: 0,
  },
  leadInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'center',
  },
  leadBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: isLightMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.2)',
    border: isLightMode ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid #6366f1',
    padding: '8px 16px',
    borderRadius: '8px',
  },
  leadTierEmoji: {
    fontSize: '1.25rem',
  },
  leadTierText: {
    color: '#6366f1',
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  lauftqText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: isLightMode ? 'rgba(26, 26, 26, 0.7)' : 'rgba(255, 255, 255, 0.7)',
  },
  onboardingGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  onboardingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
  },
  onboardingLabel: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.875rem',
  },
  onboardingValue: {
    color: isLightMode ? '#1a1a1a' : '#ffffff',
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    backgroundColor: isLightMode ? '#f9f8f5' : 'rgba(255, 255, 255, 0.05)',
    border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : 'none',
    borderRadius: '8px',
    padding: '24px',
  },
  statBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: '700',
    color: isLightMode ? '#1a1a1a' : '#ffffff',
  },
  statLabel: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.875rem',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  statDivider: {
    width: '1px',
    height: '60px',
    backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.2)' : 'rgba(255, 255, 255, 0.2)',
  },
  propertyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '16px',
  },
  propertyCard: {
    backgroundColor: isLightMode ? '#f9f8f5' : 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
    overflow: 'hidden',
    border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.2s, border-color 0.2s',
    cursor: 'pointer',
  },
  propertyImage: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
  },
  propertyInfo: {
    padding: '12px',
  },
  propertyAddress: {
    color: isLightMode ? '#1a1a1a' : '#ffffff',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '4px',
  },
  propertyPrice: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.8)' : 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.85rem',
  },
  propertyDetails: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.8rem',
    marginTop: '4px',
  },
  moreProperties: {
    marginTop: '12px',
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  // Bot transcript — kept identical to AdminUserModal / AvailableLeadModal
  viewBotButton: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1d4ed8',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  botTranscript: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '360px',
    overflowY: 'auto',
    padding: '4px',
    backgroundColor: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: '12px',
  },
  botRow: {
    display: 'flex',
    width: '100%',
  },
  botBubble: {
    maxWidth: '78%',
    backgroundColor: '#f5f4f0',
    border: '1px solid #ececec',
    borderRadius: '14px 14px 14px 4px',
    padding: '8px 12px',
  },
  botUserBubble: {
    maxWidth: '78%',
    backgroundColor: '#7a9471',
    borderRadius: '14px 14px 4px 14px',
    padding: '8px 12px',
  },
  botBubbleText: {
    fontSize: '13px',
    lineHeight: 1.45,
    color: '#1a1a1a',
    whiteSpace: 'pre-wrap',
    WebkitTextFillColor: 'currentColor',
  },
  botUserText: {
    color: '#ffffff',
    WebkitTextFillColor: '#ffffff',
  },
  botBubbleTime: {
    fontSize: '10px',
    opacity: 0.55,
    marginTop: '4px',
  },
});

export default BuyerDetailsModal;